import cx from 'classnames';
import SelectFormGroup, { SelectFormGroupV2 } from '../../common/SelectFormGroup';
import { Form } from 'react-bootstrap';
import { useCountryStateCity } from '../../hooks/useCountryStateCity';
import { useEffect, useMemo, useRef, useState } from 'react';
import PlanInfoActionCard from './PlanInfoActionCard';
import { apiUpdateOrganization } from '../../helpers/api';
import { selectBillingInfo, selectUpdateBillingStatus, subscriptionActions } from '../../redux/slicers/subscriptionSlicer';
import { useDispatch, useSelector } from 'react-redux';
import Wrapper from './Wrapper';
import './BillingForm.scss';
import Loader from '../../components/Loader/Loader';
import { isAdminOrOwner } from '../../helpers/userHelper';
import { useUserContext } from '../../contexts/UserContext';
import { warningToast } from '../../helpers/toastHelper';
import { emailIsValid } from '../../helpers/jsHelper';
import { setBillingInfo } from '../../api/subscription/subscription';
import { isNameValid } from '../../helpers/validationHelper';

const useBillingForm = ({ keepLoading = true } = {}) => {
  const initialValue = {
    billing_name: '',
    billing_email: '',
    billing_first_name: '',
    billing_last_name: '',
    billing_address: '',
    billing_street: '',
    billing_city: '',
    billing_state: '',
    billing_country: '',
    billing_pincode: '',
    billing_line1: '',
    billing_line2: '',
    billing_line3: '',
  };
  const { currentUser, orgOwnerDetail } = useUserContext();
  const [billingInputs, setBillingInputs] = useState(initialValue);
  const [errors, setErrors] = useState(initialValue);
  const [isError, setIsError] = useState(false);
  const dispatch = useDispatch();
  const [editing, setEditing] = useState(false);
  const [manualAdd, setManualAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const billingInfo = useSelector(selectBillingInfo);
  const updateBillingInfoAPI = useSelector(selectUpdateBillingStatus);

  useEffect(() => {
    dispatch(setBillingInfo());
    return () => {
      return setManualAdd(false);
    };
  }, []);

  useEffect(() => {
    if (billingInputs && !isError && updateBillingInfoAPI && isAdminOrOwner(currentUser)) {
      dispatch(subscriptionActions.setUpdateBillingInfo(false));
      onSubmit();
    }
  }, [updateBillingInfoAPI]);

  useEffect(() => {
    if (billingInputs && manualAdd) {
      dispatch(
        subscriptionActions.setBillingInfo({
          ...billingInfo,
          ...billingInputs,
          makeAPI: false,
        })
      );
    }
  }, [billingInputs]);

  const { Country, State, City, loading: locationListLoading } = useCountryStateCity();
  const countryList = Country.getAllCountries()?.map((country) => ({
    label: country.name,
    value: country.name,
    ...country,
  }));
  const updateStates = (countryId) => {
    return State.getStatesOfCountry(countryId).map((state) => ({
      label: state.name,
      value: state.name,
      ...state,
    }));
  };
  const updateCities = (countryId, stateId) => {
    if (stateId) {
      return City.getCitiesOfState(countryId, stateId).map((city) => ({
        label: city.name,
        value: city.name,
        ...city,
      }));
    } else {
      return (City.getCitiesOfCountry(countryId) || []).map((city) => ({
        label: city.name,
        value: city.name,
        ...city,
      }));
    }
  };
  const getIsoCode = (a) => {
    if (
      typeof billingInputs?.billing_country === 'string' &&
      a?.name?.toLowerCase?.() === billingInputs?.billing_country?.toLowerCase?.()
    )
      return a;

    if (billingInputs?.billing_country?.name?.toLowerCase?.() === a?.name?.toLowerCase?.()) {
      return a;
    }
  };
  const selectedCountry = useMemo(() => countryList.find(getIsoCode), [billingInputs]);
  const stateList = useMemo(
    () =>
      updateStates(selectedCountry?.isoCode)?.filter(
        (state, index, self) =>
          index === self.findIndex((t) => t.label === state.label && t.value === state.value)
      ),
    [selectedCountry]
  );
  const cityList = useMemo(
    () =>
      updateCities(
        billingInputs?.billing_country?.isoCode || null,
        billingInputs?.billing_state?.isoCode || null
      )?.filter(
        (city, index, self) =>
          index === self.findIndex((t) => t.label === city.label && t.value === city.value)
      ),
    [selectedCountry, billingInputs?.billing_country, billingInputs?.billing_state]
  );

  useEffect(() => {
    if (!billingInfo || manualAdd) return;
    const _billingInfo = {
      ...billingInfo,
      billing_country:
        countryList.find(
          (a) => a?.name?.toLowerCase?.() === billingInfo?.billing_country?.toLowerCase?.()
        ) || '',
      billing_state:
        stateList.find(
          (a) => a?.name?.toLowerCase?.() === billingInfo?.billing_state?.toLowerCase?.()
        ) || '',
      billing_city:
        cityList.find(
          (a) => a?.name?.toLowerCase?.() === billingInfo?.billing_city?.toLowerCase?.()
        ) ||
        billingInfo?.billing_city ||
        '',
    };

    setBillingInputs(_billingInfo);
  }, [billingInfo, editing]);
  const cancelEditing = (e = false) => {
    setEditing(e);
    setLoading(false);
    setErrors(initialValue);
    setIsError(false);
  };

  const validateEmail = (email) => {
    if (!email) {
      return 'Email cannot be empty';
    }
    if (!emailIsValid(email)) {
      return 'Invalid email';
    }
    return '';
  };

  const validateForm = (key, value) => {
    let errorText = { ...initialValue, ...errors };
    if (key === 'billing_name') {
      errorText[key] = !value
        ? 'Name is required!'
        : isNameValid(value)
        ? ''
        : 'Please enter a valid name';
    }
    if (key === 'billing_email') {
      errorText[key] = validateEmail(value);
    }
    if (key === 'billing_first_name') {
      errorText[key] = !value ? 'First name is required!' : '';
    }
    if (key === 'billing_country') {
      errorText[key] = !value ? 'Country is required!' : '';
    }
    if (key === 'billing_city') {
      errorText[key] = !value ? 'City is required!' : '';
    }
    if (key === 'billing_line1') {
      errorText[key] = !value ? 'Address line 1 is required!' : '';
    }
    if (key === 'billing_line3') {
      errorText[key] = !value ? 'Street address is required!' : '';
    }
    if (key === 'billing_pincode') {
      errorText[key] = !value ? 'Zipcode is required!' : '';
    }
    setErrors({ ...errorText });
    return Object.entries(errorText).filter(([k, v]) => v !== '').length !== 0;
  };

  const onSubmit = async () => {
    if (!isAdminOrOwner(currentUser)) {
      warningToast("You don't have access to update billing details.");
      return;
    }

    const _billingEmail = billingInputs?.billing_email
      ? {}
      : {
          billing_email: orgOwnerDetail?.email,
        };

    const _billingInputs = {
      ...billingInputs,
      ..._billingEmail,
      billing_country: billingInputs?.billing_country?.name || billingInputs?.billing_country || '',
      billing_state: billingInputs?.billing_state?.name || billingInputs?.billing_state || '',
      billing_city: billingInputs?.billing_city?.name || billingInputs?.billing_city || '',
    };

    if (isError) return;
    if (JSON.stringify(billingInfo) === JSON.stringify(_billingInputs)) {
      cancelEditing();
      return;
    }

    setLoading(keepLoading);
    const formData = new FormData();
    Object.entries({ ..._billingInputs, ..._billingEmail }).forEach(([key, value]) => {
      if (typeof value === 'object') {
        formData.append([key], value?.name || '');
      } else {
        formData.append([key], value || '');
      }
    });
    const res = await apiUpdateOrganization(formData);
    if (!res) return;
    dispatch(
      subscriptionActions.setBillingInfo({
        billing_name:
          `${res?.billing_first_name || ''} ${res?.billing_last_name || ''}`?.trim() || '',
        billing_email: res?.billing_email,
        billing_first_name: res?.billing_first_name || '',
        billing_last_name: res?.billing_last_name || '',
        billing_address: res?.billing_address || '',
        billing_line1: res?.billing_line1 || '',
        billing_line2: res?.billing_line2 || '',
        billing_line3: res?.billing_line3 || '',
        billing_street: res?.billing_street || '',
        billing_city: res?.billing_city || '',
        billing_state: res?.billing_state || '',
        billing_country: res?.billing_country || '',
        billing_pincode: res?.billing_pincode || '',
      })
    );
    cancelEditing();
    return res;
  };

  const handleChange = (key, value) => {
    setManualAdd(true);
    updateBillingInfoAPI && dispatch(subscriptionActions.setUpdateBillingInfo(false));
    if (key === 'billing_name') {
      const name = value?.split(' ');
      const firstName = name?.[0] || '';
      const lastName = name?.slice(1)?.join(' ') || '';
      setBillingInputs({
        ...billingInputs,
        [key]: value,
        billing_first_name: firstName,
        billing_last_name: lastName,
      });
    } else {
      setBillingInputs({
        ...billingInputs,
        [key]: value,
      });
    }

    setIsError(validateForm(key, value));
  };
  const handleBlur = (key, value) => {
    setTimeout(() => handleChange(key, value), 10);
    setIsError(validateForm(key, value));
  };

  return {
    billingInputs,
    billingInfo,

    locationListLoading,
    countryList,
    cityList,
    stateList,

    handleChange,
    handleBlur,

    errors,
    isError,

    loading,
    editing,
    cancelEditing,
    onSubmit,
  };
};

const ErrorText = () => (
  <p
    className="mb-0"
    style={{
      fontSize: '14px',
      letterSpacing: '0.32px',
      lineHeight: '16px',
      color: 'var(--error-color)',
    }}>
    Please fill the required fields
  </p>
);

export default function BillingForm() {
  const {
    billingInputs,
    billingInfo,
    locationListLoading,
    countryList,
    cityList,
    stateList,
    handleChange,
    handleBlur,
    errors,
    isError,
    loading,
    editing,
    cancelEditing,
    onSubmit,
  } = useBillingForm();
  const inputRef = useRef(null);
  const { currentUser } = useUserContext();

  const billingName = billingInfo?.billing_name;
  const addressBillingName = billingName?.trim();
  const billingAddress = [
    billingInfo?.billing_street,
    billingInfo?.billing_city?.name || billingInfo?.billing_city,
    billingInfo?.billing_state?.name || billingInfo?.billing_state,
    billingInfo?.billing_country?.name || billingInfo?.billing_country,
    billingInfo?.billing_pincode,
  ];
  const address = billingAddress.filter(Boolean).join(', ');
  const billingDetails = (
    <>
      {addressBillingName ? (
        <>
          {billingName}
          <br />
        </>
      ) : null}
      {address}
    </>
  );

  return (
    <PlanInfoActionCard
      title="Billing details"
      description={
        !editing
          ? addressBillingName || address
            ? billingDetails
            : 'Please add billing address.'
          : ''
      }
      actionText={editing ? 'Cancel' : 'Edit'}
      onClick={() => {
        if (!isAdminOrOwner(currentUser)) {
          warningToast("You don't have access to update billing details.");
          return;
        }
        cancelEditing(!editing);
        !editing && inputRef?.current?.focus();
      }}
      actionText2={editing ? 'Save' : ''}
      onClick2={onSubmit}>
      {editing && (
        <Wrapper className="p-4" style={{ border: '1px solid var(--dark-500)' }}>
          {isError && !loading ? <ErrorText /> : null}
          {loading && <Loader position="absolute" loading backdrop />}
          <Form className="w-100 billing-form" onSubmit={onSubmit}>
            <Form.Group className="form-outline">
              <SelectFormGroupV2
                error={errors.billing_country}
                name="billing_country"
                options={countryList}
                value={billingInputs.billing_country}
                onChange={(v) => {
                  handleChange('billing_country', v);
                  setTimeout(() => {
                    handleBlur('billing_country', v);
                  }, 10);
                }}
                // onBlur={(v) => handleBlur('billing_country', v)}
                placeholder="Country"
                isLoading={locationListLoading}
              />
            </Form.Group>
            <Form.Group className="form-outline inline">
              <Form.Control
                ref={inputRef}
                type="text"
                maxLength={50}
                placeholder="First name"
                value={billingInputs.billing_first_name}
                className={cx(errors.billing_first_name && 'input-error')}
                name="billing_first_name"
                onChange={(v) => handleChange('billing_first_name', v.target.value)}
                onBlur={(v) => handleBlur('billing_first_name', v.target.value)}
              />
            </Form.Group>
            <Form.Group className="form-outline inline">
              <Form.Control
                type="text"
                maxLength={50}
                placeholder="Last name"
                value={billingInputs.billing_last_name}
                className={cx(errors.billing_last_name && 'input-error')}
                name="billing_last_name"
                onChange={(v) => handleChange('billing_last_name', v.target.value)}
                onBlur={(v) => handleBlur('billing_last_name', v.target.value)}
              />
            </Form.Group>
            <Form.Group className="form-outline">
              <Form.Control
                type="text"
                maxLength={50}
                placeholder="Address line 1"
                value={billingInputs.billing_line1}
                className={cx(errors.billing_line1 && 'input-error')}
                name="billing_line1"
                onChange={(v) => handleChange('billing_line1', v.target.value)}
                onBlur={(v) => handleBlur('billing_line1', v.target.value)}
              />
            </Form.Group>
            <Form.Group className="form-outline">
              <Form.Control
                type="text"
                maxLength={50}
                placeholder="Address line 2"
                value={billingInputs.billing_line2}
                className={cx(errors.billing_line2 && 'input-error')}
                name="billing_line2"
                onChange={(v) => handleChange('billing_line2', v.target.value)}
                onBlur={(v) => handleBlur('billing_line2', v.target.value)}
              />
            </Form.Group>
            <Form.Group className="form-outline">
              <Form.Control
                type="text"
                maxLength={50}
                placeholder="Street Address"
                value={billingInputs.billing_line3}
                className={cx(errors.billing_line3 && 'input-error')}
                name="billing_line3"
                onChange={(v) => handleChange('billing_line3', v.target.value)}
                onBlur={(v) => handleBlur('billing_line3', v.target.value)}
              />
            </Form.Group>
            <Form.Group className="form-outline inline">
              <SelectFormGroupV2
                error={errors.billing_city}
                name={'billing_city'}
                options={cityList}
                value={billingInputs.billing_city}
                onChange={(v) => {
                  handleChange('billing_city', v);
                  setTimeout(() => {
                    handleBlur('billing_city', v);
                  }, 10);
                }}
                // onBlur={(v) => handleBlur('billing_city', v)}
                placeholder="City"
                isLoading={locationListLoading}
              />
            </Form.Group>
            <Form.Group className="form-outline inline">
              <Form.Control
                type="text"
                maxLength={50}
                placeholder="Zipcode"
                value={billingInputs.billing_pincode}
                className={cx(errors.billing_pincode && 'input-error')}
                name="billing_pincode"
                onChange={(v) => handleChange('billing_pincode', v.target.value)}
                onBlur={(v) => handleBlur('billing_pincode', v.target.value)}
              />
            </Form.Group>
            <Form.Group className="form-outline">
              <SelectFormGroupV2
                error={errors.billing_state}
                name={'billing_state'}
                options={stateList}
                value={billingInputs.billing_state}
                onChange={(v) => {
                  handleChange('billing_state', v);
                  setTimeout(() => {
                    handleBlur('billing_state', v);
                  }, 10);
                }}
                // onBlur={(v) => handleBlur('billing_state', v)}
                placeholder="State"
                isLoading={locationListLoading}
              />
            </Form.Group>
          </Form>
        </Wrapper>
      )}
    </PlanInfoActionCard>
  );
}

export function BillingFormV2({ planInfoProps = {} }) {
  const {
    billingInputs,
    countryList,
    handleChange,
    handleBlur,
    errors,
    isError,
    loading,
    onSubmit,
  } = useBillingForm({ keepLoading: false });
  const { currentUser } = useUserContext();
  const inputRef = useRef(null);

  const _handleChange = (key, value) => {
    if (!isAdminOrOwner(currentUser)) {
      warningToast("You don't have access to update billing details.");
      return;
    }
    if (['billing_name', 'billing_pincode'].includes(key)) {
      if (!isNameValid(value)) return;
    }
    handleChange(key, value);
  };

  const onFormSubmit = () => {
    if (!isAdminOrOwner(currentUser)) {
      warningToast("You don't have access to update billing details.");
      return;
    }
    onSubmit();
  };

  return (
    <PlanInfoActionCard title="Billing details" {...planInfoProps} keepOpen={loading || true}>
      {/* {isError && !loading ? <ErrorText /> : null} */}
      {loading && <Loader position="absolute" loading backdrop />}
      <Form className="w-100 billing-form billing-form-v2" onSubmit={onFormSubmit}>
        <Form.Group className="form-outline">
          <Form.Control
            ref={inputRef}
            type="text"
            maxLength={50}
            placeholder="Name*"
            value={billingInputs.billing_name}
            className={cx(errors.billing_name && 'input-error')}
            name="billing_name"
            onChange={(v) => _handleChange('billing_name', v.target.value)}
            // onBlur={(v) => {
            //   isAdminOrOwner(currentUser) && handleBlur('billing_name', v.target.value);
            //   // onFormSubmit();
            // }}
          />
          {errors?.billing_name && <span className="error-text-red">{errors?.billing_name} </span>}
        </Form.Group>
        <Form.Group className="form-outline">
          <Form.Control
            type="text"
            maxLength={50}
            placeholder="Email address*"
            value={billingInputs.billing_email}
            className={cx(errors.billing_email && 'input-error')}
            name="billing_email"
            onChange={(v) => _handleChange('billing_email', v.target.value)}
            // onBlur={(v) => {
            //   isAdminOrOwner(currentUser) && handleBlur('billing_email', v.target.value);
            //   // onFormSubmit();
            // }}
          />
          {errors?.billing_email && (
            <span className="error-text-red">{errors?.billing_email} </span>
          )}
        </Form.Group>
        <Form.Group className="form-outline">
          <Form.Control
            type="text"
            maxLength={50}
            placeholder="Address line 1*"
            value={billingInputs?.billing_line1}
            className={cx(errors?.billing_line1 && 'input-error')}
            name="billing_line1"
            onChange={(v) => _handleChange('billing_line1', v.target.value)}
            // onBlur={(v) => {
            //   isAdminOrOwner(currentUser) && handleBlur('billing_line1', v.target.value);
            // }}
          />
          {errors?.billing_line1 && (
            <span className="error-text-red">{errors?.billing_line1} </span>
          )}
        </Form.Group>
        <Form.Group className="form-outline">
          <Form.Control
            type="text"
            maxLength={50}
            placeholder="Address line 2"
            value={billingInputs?.billing_line2}
            className={cx(errors?.billing_line2 && 'input-error')}
            name="billing_line2"
            onChange={(v) => _handleChange('billing_line2', v.target.value)}
            // onBlur={(v) => {
            //   isAdminOrOwner(currentUser) && handleBlur('billing_line2', v.target.value);
            // }}
          />
          {/* {errors?.billing_line2 && (
            <span className="error-text-red">{errors?.billing_line2} </span>
          )} */}
        </Form.Group>
        <div className="d-flex flex-wrap">
          <Form.Group className="form-outline inline">
            <Form.Control
              type="text"
              maxLength={50}
              placeholder="Street address*"
              value={billingInputs?.billing_line3}
              className={cx(errors?.billing_line3 && 'input-error')}
              name="billing_line3"
              onChange={(v) => _handleChange('billing_line3', v.target.value)}
              // onBlur={(v) => {
              //   isAdminOrOwner(currentUser) && handleBlur('billing_line3', v.target.value);
              //   // onFormSubmit();
              // }}
            />
            {errors?.billing_line3 && (
              <span className="error-text-red">{errors?.billing_line3} </span>
            )}
          </Form.Group>
          <Form.Group className="form-outline inline">
            <Form.Control
              type="text"
              maxLength={50}
              placeholder="City*"
              value={billingInputs.billing_city?.name || billingInputs.billing_city}
              className={cx(errors.billing_city && 'input-error')}
              name="billing_city"
              onChange={(v) => _handleChange('billing_city', v.target.value)}
              // onBlur={(v) => {
              //   isAdminOrOwner(currentUser) && handleBlur('billing_city', v.target.value);
              // }}
            />
            {errors?.billing_city && (
              <span className="error-text-red">{errors?.billing_city} </span>
            )}
          </Form.Group>
        </div>
        <div className="d-flex flex-wrap gap-sm-3">
          <Form.Group className="form-outline inline m-0">
            <SelectFormGroup
              id="billing_country"
              name="billing_country"
              options={countryList}
              value={billingInputs.billing_country}
              groupClassName="form-outline p-0"
              onChangeCallback={(v) => {
                handleChange('billing_country', v, true);
              }}
              // onBlurCallback={(v) => {
              //   // handleBlur('billing_country', v);
              //   // onFormSubmit();
              // }}
              placeholder="Country*"
            />
            {errors?.billing_country && (
              <p className="error-text-red">{errors?.billing_country} </p>
            )}
          </Form.Group>
          <Form.Group className="form-outline inline">
            <Form.Control
              type="text"
              maxLength={50}
              placeholder="Zipcode*"
              value={billingInputs.billing_pincode}
              className={cx(errors.billing_pincode && 'input-error')}
              name="billing_pincode"
              onChange={(v) => _handleChange('billing_pincode', v.target.value)}
              // onBlur={(v) => {
              //   isAdminOrOwner(currentUser) && handleBlur('billing_pincode', v.target.value);
              //   // onFormSubmit();
              // }}
            />
            {errors?.billing_pincode && (
              <span className="error-text-red">{errors?.billing_pincode} </span>
            )}
          </Form.Group>
        </div>
      </Form>
    </PlanInfoActionCard>
  );
}